/**
 * Insights homepage atoms — aligned to ledger8760 design-system standards.
 *
 * Typography routes through the theme variant ramp (dataLabel / h2 / body2 /
 * hero), never raw fontSize. Status/code labels use MUI <Chip> with
 * theme.palette + spectrum tints — the same convention as <PerformanceCell>
 * and <ComplianceStandard>. No monospace (not part of the design system).
 *
 * KPI tiles are NOT here — use the existing <Metric> component. The only
 * genuinely-additive piece is the semantic Card variant set (top-action /
 * demoted / success / warning accents) layered on MUI Card.
 */
import type React from "react"

import { Chip, Card as MuiCard, Typography } from "@mui/material"
import type { CardProps as MuiCardProps } from "@mui/material/Card"
import type { SxProps, Theme } from "@mui/material/styles"
import { useTheme } from "@mui/material/styles"

// ── cerulean accent ──────────────────────────────────────────────────────────
// WATCH-zone accent token. Real theme exposes it at palette.spectrum.cerulean.
export const cerulean = (theme: Theme) => ({
  main: theme.palette.spectrum.cerulean[500], // #046381
  light: theme.palette.spectrum.cerulean[100],
  dark: theme.palette.spectrum.cerulean[700],
  surface: theme.palette.spectrum.cerulean[25],
})

// ── eyebrow ──────────────────────────────────────────────────────────────────
// Uppercase label on the `dataLabel` (10px) ramp variant.
export const Eyebrow: React.FC<
  React.PropsWithChildren<{ sx?: SxProps<Theme> }>
> = ({ children, sx }) => (
  <Typography
    component="span"
    sx={[
      {
        display: "inline-block",
        textTransform: "uppercase",
        fontWeight: "bold",
        letterSpacing: "0.06em",
        color: "text.secondary",
      },
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
    variant="dataLabel"
  >
    {children}
  </Typography>
)

// ── section-title ──────────────────────────────────────────────────────────
// Prominent zone heading on the `h2` (20px) ramp variant.
export const SectionTitle: React.FC<React.PropsWithChildren> = ({
  children,
}) => (
  <Typography component="h2" sx={{ fontWeight: "bold" }} variant="h2">
    {children}
  </Typography>
)

// ── confidence-chip ──────────────────────────────────────────────────────────
// MUI Chip + spectrum tint, mirroring <PerformanceCell>'s status convention.
export const ConfidenceChip: React.FC<{
  level: "HIGH" | "MEDIUM" | "LOW"
}> = ({ level }) => (
  <Chip
    color={
      level === "HIGH" ? "success" : level === "MEDIUM" ? "warning" : "default"
    }
    label={level}
    size="small"
    sx={{
      letterSpacing: "0.04em",
      bgcolor: (theme) =>
        level === "HIGH"
          ? theme.palette.spectrum.green[50]
          : level === "MEDIUM"
            ? theme.palette.spectrum.mustard[50]
            : undefined,
    }}
    variant="outlined"
  />
)

// ── detector-tag ─────────────────────────────────────────────────────────────
// Compact code label (E2 / E3 / C1) as a small Chip. Emphasized = primary.
export const DetectorTag: React.FC<{
  code: string
  emphasized?: boolean
}> = ({ code, emphasized }) => (
  <Chip
    color={emphasized ? "primary" : "default"}
    label={code}
    size="small"
    sx={{ letterSpacing: "0.04em", fontWeight: "bold" }}
    variant={emphasized ? "filled" : "outlined"}
  />
)

// ── card (semantic-variant wrapper over MUI Card) ──────────────────────────
type CardVariantOverride =
  | "top-action"
  | "demoted"
  | "success"
  | "warning"
  | "default"

export interface CardProps extends Omit<MuiCardProps, "variant"> {
  variant?: MuiCardProps["variant"] | CardVariantOverride
}

const isVariantOverride = (v: CardProps["variant"]): v is CardVariantOverride =>
  v === "top-action" ||
  v === "demoted" ||
  v === "success" ||
  v === "warning" ||
  v === "default"

/**
 * Pure: semantic-variant → sx. Keeps render side-effect-free.
 *
 * Chrome follows the platform card language (MuiPaper: elevation-2 shadow,
 * 8px theme radius, NO 1px borders — see ReportingCard / PageCard). The one
 * border is the deliberate accent from the information-budget spec: a 4px
 * primary left stripe on the page's single primary element. Demoted/tinted
 * variants are flat contained surfaces (the Metric / variant="contained"
 * pattern), not bordered boxes.
 */
const variantSx = (
  theme: Theme,
  variant: CardProps["variant"]
): SxProps<Theme> => {
  switch (variant) {
    case "top-action":
      return {
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        boxShadow: theme.shadows[2],
      }
    case "demoted":
      return {
        bgcolor: theme.palette.grey[25],
        boxShadow: "none",
      }
    case "success":
      return {
        bgcolor: theme.palette.success.light,
        boxShadow: "none",
      }
    case "warning":
      return {
        bgcolor: theme.palette.warning.light,
        boxShadow: "none",
      }
    default:
      return {
        boxShadow: theme.shadows[2],
      }
  }
}

export const Card: React.FC<CardProps> = ({
  variant,
  sx,
  children,
  ...rest
}) => {
  const theme = useTheme()
  const override = isVariantOverride(variant)

  return (
    <MuiCard
      // Elevation surface; visuals come from variantSx so the semantic
      // variants stay single-source. Radius inherits the theme's 8px Paper
      // override.
      elevation={0}
      sx={[
        variantSx(theme, variant),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      variant={override ? "elevation" : (variant as MuiCardProps["variant"])}
      {...rest}
    >
      {children}
    </MuiCard>
  )
}
